import { css, html, nothing, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
   AtlTableCell,
   AtlTableRow,
   AtlTableProps,
   AtlTableHeaderCell,
} from './table.props';
import { classMap } from 'lit/directives/class-map.js';
import { booleanAttributeConverter } from '../helpers';
import { getGlobalStyleSheets } from '../../global-styles';
import { SpinnerAlignment, SpinnerVariant, SpinnerSize } from '../spinner';

import * as TagNames from './table.tagnames';
import { AtlTableSortEvent } from './table.events';

@customElement(TagNames.Table)
export class AtlTable extends LitElement implements AtlTableProps {
   declare tableId: string;
   declare loading: boolean;
   declare headers: AtlTableHeaderCell[];
   declare rows: AtlTableRow[];
   declare tableClassName?: string;
   declare sortedBy?: string;
   declare sortedAscending?: boolean;
   declare noResultsText?: string;

   static properties = {
      tableId: { type: String },
      loading: { type: Boolean, convertor: booleanAttributeConverter() },
      headers: { type: Object },
      rows: { type: Object },
      tableClassName: { type: String },
      sortedBy: { type: String },
      sortedAscending: {
         type: Boolean,
         convertor: booleanAttributeConverter(),
      },
      noResultsText: { type: String },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .atl-table-header-loading {
            opacity: 0.4;
         }
      `,
   ];

   renderSortIcon(header: AtlTableHeaderCell) {
      if (!this.sortedBy || header.id !== this.sortedBy) return nothing;

      const iconClass = this.sortedAscending
         ? 'fa-arrow-up-wide-short'
         : 'fa-arrow-down-wide-short';

      return html`
         <i
            class=${classMap({
               'fa-solid': true,
               [iconClass]: true,
               'kpmg': true,
               'icon': true,
               'pt-1': true
            })}
            part="icon"
         ></i>
      `;
   }

   renderHeaders() {
      return this.headers
         ?.filter((h) => !h.isHidden)
         .map((h: AtlTableHeaderCell) => {
            return html`
               <th
                  class="${h.cellClasses || ''} ${h.sortable ? 'sortable' : ''}"
                  @click=${(e: Event) => this._handleSortingClick(e, h.id)}
               >
                  <div style="display: flex; justify-content: space-between;">
                     <slot name=${`${h.id}`}></slot>
                     ${this.renderSortIcon(h)}
                  </div>
               </th>
            `;
         });
   }

   renderCells(row: AtlTableRow) {
      return row?.cells
         ?.filter((h) => !h.isHidden)
         ?.map((c: AtlTableCell) => {
            return html`
               <td class=${c.cellClasses || ''}>
                  <slot name=${`${row.id}${c.id}`}></slot>
               </td>
            `;
         });
   }

   renderSpinner() {
      if (!this.loading) return nothing;

      return html`
         <div>
            <atlantis-spinner
               size=${SpinnerSize.large}
               align=${SpinnerAlignment.Middle}
               color=${SpinnerVariant.Primary}
            >
            </atlantis-spinner>
         </div>
      `;
   }

   renderRows() {
      if (this.rows?.length > 0) {
         return this.rows?.map((r: AtlTableRow) => {
            return html`
               <tr
                  class=${classMap({
                     'atl-table-header': true,
                     'atl-table-header-loading': this.loading,
                  })}
               >
                  ${this.renderCells(r)}
               </tr>
            `;
         });
      }

      return html`
         <tr
            class=${classMap({
               'atl-table-header': true,
               'atl-table-header-loading': this.loading,
            })}
         >
            <td class="text-center" colspan=${this.headers?.length ?? 1}>
               ${this.noResultsText ?? 'No results to show.'}
            </td>
         </tr>
      `;
   }

   render() {
      return html`
         <div class="datatable-container">
            <div
               id=${this.tableId}
               class="datatable-responsive ${this.tableClassName || ''}"
            >
               ${this.renderSpinner()}
               <table class="table datatable">
                  <thead>
                     <tr class="atl-table-header">
                        ${html`${this.renderHeaders()}`}
                     </tr>
                  </thead>
                  <tbody>
                     ${html`${this.renderRows()}`}
                  </tbody>
               </table>
            </div>
         </div>
      `;
   }

   protected _handleSortingClick(e: Event, headerId: string) {
      if (
         !this.sortedBy ||
         !this.headers.find((h) => h.id === headerId)?.sortable
      )
         return;

      e.stopPropagation();
      this.dispatchEvent(new AtlTableSortEvent(headerId));
   }
}
